/**
 * False-positive-rate helpers for the Evaluation Threshold card.
 *
 * FPR = FP / (FP + TN). Confusion-matrix cells arrive in two shapes:
 *   - plain scalars from ``/compute-metrics-for-thresholds`` and the PR-curve
 *     candidates (``{ tn, fp, fn, tp }``), and
 *   - ``{ oldModel, newModel }`` comparison objects from the stored evaluation
 *     metrics (``all_metrics.eval_class_metrics[label].confusion_matrix``).
 */

const cell = (v) => {
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object') return cell(v.newModel);
  return undefined;
};

const round3 = (n) => Math.round(n * 1000) / 1000;

/**
 * FP / (FP + TN) for a confusion matrix, or ``null`` when either cell is
 * missing or there are no negatives (FP + TN === 0, e.g. NER where TN is 0 by
 * convention).
 */
export const falsePositiveRate = (confusionMatrix) => {
  if (!confusionMatrix || typeof confusionMatrix !== 'object') return null;
  const fp = cell(confusionMatrix.fp);
  const tn = cell(confusionMatrix.tn);
  if (!Number.isFinite(fp) || !Number.isFinite(tn)) return null;
  const negatives = fp + tn;
  if (negatives <= 0) return null;
  return fp / negatives;
};

/**
 * Threshold to use for a target FPR. FPR is step-wise and non-increasing in
 * the threshold, so many thresholds share the same FPR; pick the LOWEST
 * candidate threshold whose FPR does not exceed the target (never over the
 * budget, recall as high as possible). When no candidate meets the target
 * (target below the minimum achievable FPR), fall back to the highest
 * threshold, which has the lowest FPR.
 *
 * Returns ``null`` when no candidate carries a usable confusion matrix.
 */
export const thresholdForFpr = (candidates, targetFpr) => {
  const target = Number(targetFpr);
  if (!Array.isArray(candidates) || !Number.isFinite(target)) return null;
  const scored = candidates
    .map((c) => ({ threshold: Number(c?.threshold), fpr: falsePositiveRate(c?.confusion_matrix) }))
    .filter((c) => Number.isFinite(c.threshold) && c.fpr != null);
  if (!scored.length) return null;
  const within = scored.filter((c) => c.fpr <= target);
  if (within.length) {
    return within.reduce((best, c) => (c.threshold < best.threshold ? c : best)).threshold;
  }
  return scored.reduce((best, c) => (c.threshold > best.threshold ? c : best)).threshold;
};

/** Keep only digits and a single decimal point while the user types. */
export const sanitizeDecimalInput = (text) => {
  const raw = String(text ?? '').replace(/[^0-9.]/g, '');
  const firstDot = raw.indexOf('.');
  if (firstDot === -1) return raw;
  return raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
};

/**
 * Parse a typed value into [0, 1] (rounded to 3 decimals). Values above 1
 * clamp to 1, values below 0 clamp to 0; empty or non-numeric input returns
 * ``fallback``.
 */
export const parseUnitInterval = (text, fallback) => {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '' || trimmed === '.') return fallback;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return fallback;
  return round3(Math.min(1, Math.max(0, n)));
};
